import QRCode from "qrcode";
import { type FormEvent, useEffect, useState } from "react";
import { authClient } from "../api.ts";
import { Aviso, Boton, Campo, CLASE_ENTRADA } from "./ui.tsx";

/**
 * Segundo factor (fase 6): la aplicación de autenticación de la persona (Google Authenticator,
 * Microsoft Authenticator, Aegis…). Dos piezas que se usan en dos sitios: activarlo (en "Mi
 * cuenta", o al entrar desde fuera sin tenerlo) y dar el código (al entrar desde fuera). Dentro
 * de la red nunca se pide: eso lo decide la API, acá solo se muestra lo que falta.
 */

type Resultado = {
  error?: { status?: number | undefined; message?: string | undefined } | null | undefined;
};

/** El texto para la persona según cómo respondió la API al código. */
function mensajeDeCodigo(r: Resultado): string {
  const estado = r.error?.status;
  if (estado === 401) return "El código no es correcto. Mira el de ahora en tu aplicación.";
  if (estado === 429) return r.error?.message ?? "Demasiados intentos. Espera unos minutos.";
  return r.error?.message ?? "No se pudo verificar el código. Prueba de nuevo.";
}

/**
 * Activar: contraseña → QR y códigos de respaldo → primer código. Hasta que la persona confirma
 * el primer código no queda activado (y si cierra a medias, vuelve a empezar sin daño).
 */
export function ActivarSegundoFactor({ onActivado }: { onActivado: () => void }) {
  const [contrasena, setContrasena] = useState("");
  const [preparado, setPreparado] = useState<{ uri: string; respaldo: string[] } | null>(null);
  const [codigo, setCodigo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  async function preparar(e: FormEvent) {
    e.preventDefault();
    if (!contrasena) {
      setError("Confirma con tu contraseña");
      return;
    }
    setError(null);
    setEnviando(true);
    const r = await authClient.twoFactor.enable({ password: contrasena });
    setEnviando(false);
    setContrasena("");
    if (r.error) {
      setError(
        r.error.status === 400
          ? "La contraseña no es correcta"
          : (r.error.message ?? "No se pudo empezar la activación"),
      );
      return;
    }
    const datos = r.data as { totpURI?: string; backupCodes?: string[] } | null;
    if (!datos?.totpURI) {
      setError("La respuesta no trajo el código QR. Prueba de nuevo.");
      return;
    }
    setPreparado({ uri: datos.totpURI, respaldo: datos.backupCodes ?? [] });
  }

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    const limpio = codigo.replace(/\s/g, "");
    if (!/^\d{6}$/.test(limpio)) {
      setError("Escribe los seis dígitos que muestra la aplicación");
      return;
    }
    setError(null);
    setEnviando(true);
    const r = await authClient.twoFactor.verifyTotp({ code: limpio });
    setEnviando(false);
    if (r.error) {
      setError(mensajeDeCodigo(r));
      return;
    }
    setListo(true);
    onActivado();
  }

  if (listo) {
    return (
      <div className="rounded-md bg-verde-fondo px-3 py-2 text-[12.5px] text-verde">
        Segundo factor activado. Desde fuera de la red, al entrar te pediremos el código de la
        aplicación.
      </div>
    );
  }

  if (!preparado) {
    return (
      <form onSubmit={preparar} className="space-y-3">
        <p className="text-[12.5px] text-texto-3">
          Instala una aplicación de autenticación en tu celular (Google Authenticator, Microsoft
          Authenticator o similar). Confirma con tu contraseña para generar el código QR.
        </p>
        <Campo etiqueta="Contraseña">
          <input
            type="password"
            autoComplete="current-password"
            value={contrasena}
            onChange={(e) => setContrasena(e.target.value)}
            className={CLASE_ENTRADA}
          />
        </Campo>
        {error && <Aviso>{error}</Aviso>}
        <Boton type="submit" disabled={enviando}>
          {enviando ? "Preparando…" : "Generar el código QR"}
        </Boton>
      </form>
    );
  }

  return (
    <form onSubmit={confirmar} className="space-y-4">
      <ol className="list-decimal space-y-4 pl-5 text-[12.5px] text-texto-2">
        <li>
          <div className="mb-2">Escanea este código con la aplicación de autenticación:</div>
          <CodigoQr uri={preparado.uri} />
          <details className="mt-2 text-[11.5px] text-texto-3">
            <summary className="cursor-pointer">No puedo escanearlo</summary>
            <div className="mt-1">
              Escribe esta clave a mano en la aplicación:{" "}
              <code className="rounded bg-fondo px-1.5 py-0.5 font-mono text-[12px] text-texto select-all">
                {secretoDe(preparado.uri)}
              </code>
            </div>
          </details>
        </li>
        <li>
          <div className="mb-2">
            Guarda estos códigos de respaldo en un lugar seguro. Cada uno sirve una sola vez, para
            entrar si no tienes el celular a mano. No se vuelven a mostrar.
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-borde bg-fondo px-3 py-2 font-mono text-[12.5px] text-texto sm:grid-cols-4">
            {preparado.respaldo.map((c) => (
              <li key={c} className="select-all">
                {c}
              </li>
            ))}
          </ul>
        </li>
        <li>
          <div className="mb-2">Escribe el código de seis dígitos que muestra la aplicación:</div>
          <EntradaDeCodigo valor={codigo} onCambio={setCodigo} />
        </li>
      </ol>
      {error && <Aviso>{error}</Aviso>}
      <Boton type="submit" disabled={enviando}>
        {enviando ? "Comprobando…" : "Confirmar y activar"}
      </Boton>
    </form>
  );
}

/** Dar el código con la sesión abierta (al entrar desde fuera). Con respaldo si no hay celular. */
export function VerificarSegundoFactor({ onVerificado }: { onVerificado: () => void }) {
  const [codigo, setCodigo] = useState("");
  const [conRespaldo, setConRespaldo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const limpio = codigo.trim();
    if (!conRespaldo && !/^\d{6}$/.test(limpio.replace(/\s/g, ""))) {
      setError("Escribe los seis dígitos que muestra la aplicación");
      return;
    }
    if (conRespaldo && !limpio) {
      setError("Escribe uno de tus códigos de respaldo");
      return;
    }
    setError(null);
    setEnviando(true);
    const r = conRespaldo
      ? await authClient.twoFactor.verifyBackupCode({ code: limpio })
      : await authClient.twoFactor.verifyTotp({ code: limpio.replace(/\s/g, "") });
    setEnviando(false);
    if (r.error) {
      setError(
        conRespaldo && r.error.status === 401
          ? "Ese código de respaldo no sirve: o no es correcto o ya se usó."
          : mensajeDeCodigo(r),
      );
      return;
    }
    onVerificado();
  }

  return (
    <form onSubmit={enviar} className="space-y-4">
      {conRespaldo ? (
        <Campo
          etiqueta="Código de respaldo"
          ayuda="Uno de los que guardaste al activar; se gasta al usarlo."
        >
          <input
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            autoComplete="off"
            className={`${CLASE_ENTRADA} font-mono`}
            placeholder="xxxxx-xxxxx"
          />
        </Campo>
      ) : (
        <Campo etiqueta="Código de la aplicación">
          <EntradaDeCodigo valor={codigo} onCambio={setCodigo} />
        </Campo>
      )}
      {error && <Aviso>{error}</Aviso>}
      <Boton type="submit" disabled={enviando} className="w-full">
        {enviando ? "Comprobando…" : "Continuar"}
      </Boton>
      <button
        type="button"
        onClick={() => {
          setConRespaldo((v) => !v);
          setCodigo("");
          setError(null);
        }}
        className="block w-full text-center text-[12px] text-texto-3 underline-offset-2 hover:underline"
      >
        {conRespaldo
          ? "Usar el código de la aplicación"
          : "No tengo el celular: usar un código de respaldo"}
      </button>
    </form>
  );
}

function EntradaDeCodigo({ valor, onCambio }: { valor: string; onCambio: (v: string) => void }) {
  return (
    <input
      value={valor}
      onChange={(e) => onCambio(e.target.value.replace(/[^\d\s]/g, "").slice(0, 7))}
      inputMode="numeric"
      autoComplete="one-time-code"
      aria-label="Código de seis dígitos"
      className={`${CLASE_ENTRADA} max-w-[200px] font-mono text-[18px] tracking-[.3em]`}
      placeholder="123 456"
    />
  );
}

/** El QR del otpauth://, dibujado en el navegador: el secreto no sale a ningún servicio. */
function CodigoQr({ uri }: { uri: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let vigente = true;
    QRCode.toString(uri, { type: "svg", margin: 1, width: 180 })
      .then((s) => vigente && setSvg(s))
      .catch(() => vigente && setSvg(null));
    return () => {
      vigente = false;
    };
  }, [uri]);
  if (!svg) return <div className="size-[180px] rounded-md border border-borde bg-fondo" />;
  return (
    <img
      src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
      alt="Código QR para la aplicación de autenticación"
      width={180}
      height={180}
      className="rounded-md border border-borde bg-white"
    />
  );
}

function secretoDe(uri: string): string {
  try {
    return new URL(uri).searchParams.get("secret") ?? "";
  } catch {
    return "";
  }
}
